<template>
  <v-container class="modern-view pb-12">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-file-invoice-dollar</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Facturación por <span class="secondary--text">Producto</span></div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Análisis de ingresos facturados por catálogo de productos</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-product-invoice :filter='filter' :options='options'/>
      </div>
    </v-row>

    <!-- Modern Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="end">
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Emitida desde</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_after' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Emitida hasta</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_before' 
            clearable 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="4">
          <Can I="readAll" a="SalesNote">
            <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Vendedor</div>
            <v-select
              v-model="filter.seller"
              :items="sellers"
              item-text="email"
              item-value="id"
              outlined
              clearable
              dense
              hide-details
              :disabled="loading"
              class="modern-input"
            >
              <template v-slot:item="{ item }">
                <v-avatar size="24" color="primary lighten-5" class="mr-3">
                  <span class="primary--text font-weight-bold" style="font-size: 10px;">{{ item.first_name[0] }}</span>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span class="body-2 font-weight-bold">{{ item.first_name }} {{ item.last_name }}</span>
                  <span class="caption grey--text">{{ item.email }}</span>
                </div>
              </template>
            </v-select>
          </Can>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" md="auto" class="d-flex pb-1">
          <v-btn color="primary" icon class="grey lighten-4 shadow-xs" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        class="modern-table clickable-rows"
        @click:row='displayDetail'
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Header -->
        <template v-slot:top>
          <v-sheet color="bg-lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Consolidado de Facturación ({{ totalItems }})</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por código, descripción, familia..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </v-sheet>
        </template>

        <template v-slot:item.product__sku="{ item }">
          <v-chip x-small label dark color="secondary" class="font-weight-black">{{ item.product__sku }}</v-chip>
        </template>

        <template v-slot:item.product__name="{ item }">
          <div class="body-2 font-weight-bold primary--text lh-1-2 py-2">{{ item.product__name }}</div>
        </template>

        <template v-slot:item.product__family__name="{ item }">
          <span class="caption grey--text font-weight-bold">{{ item.product__family__name }}</span>
        </template>

        <template v-slot:item.total="{ item }">
          <div class="body-1 font-weight-black secondary--text tracking-tighter">{{ item.total | moneda }}</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- detail Dialog -->
    <v-dialog v-model="toDetail" max-width="1280" scrollable transition="dialog-bottom-transition">
      <v-card class="modern-card shadow-lg">
        <v-toolbar flat class="border-bottom">
          <v-avatar color="primary lighten-5" size="32" class="mr-3">
            <v-icon color="primary" x-small>fas fa-info-circle</v-icon>
          </v-avatar>
          <v-toolbar-title class="body-1 font-weight-black primary--text uppercase">
            Detalle de Facturas: <span class="secondary--text ml-2">{{ item.product__name }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="toDetail = false" class="grey lighten-4">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        
        <v-card-text class="pa-6">
          <product-invoice-detail v-if="toDetail" :product="item" :filter="filter"/>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {fetchInvoicedProducts} from '@/api/reports'
import {fetchSellers} from '@/api/auth'
import ProductInvoiceDetail from '@/components/reports/ProductInvoiceDetail'
import ExportProductInvoice from '@/components/reports/ExportProductInvoice'
import moment from 'moment'
import notifications from '@/utils/notifications'

export default {
  name:"ProductInvoiceReport",
  components:{ProductInvoiceDetail,ExportProductInvoice},
  data() {
    return {
      loading: false,
      sellers: [],
      filter:{
        created_after:moment().startOf('month').format('YYYY-MM-DD'),
        created_before:'',
        seller:''
      },
      totalItems:0,
      search:'',
      options:{},
      items:[],
      timer: null,
      iva:this.$store.getters.getCompany.tax,
      headers:[
        {text: 'Código', align: 'start', value: 'product__sku', class: 'table-header', width: '130px'},
        {text: 'Descripción', align: 'start', value: 'product__name', class: 'table-header'},
        {text: 'Familia', value:'product__family__name', class: 'table-header', width: '150px'},
        {text: 'Línea', align: 'start', value: 'product__line__name', class: 'table-header', width: '150px'},
        {text: 'Neto Facturado', align: 'end', value: 'total', class: 'table-header', width: '160px'},
      ],
      toDetail:false,
      item:''
    }
  },
  async mounted(){
    this.loading = true
    try {
      this.sellers = await fetchSellers()
      // We don't call fetch() here because the options watcher 
      // of the v-data-table will trigger the initial load.
    } catch (err) {
      notifications.showError(err)
    } finally {
      this.loading = false
    }
  },
  methods:{
    async fetch(){
      this.loading=true
      if(!this.filter.created_after) this.filter.created_after=''
      if(!this.filter.created_before) this.filter.created_before=''
      if(!this.filter.seller) this.filter.seller=''
      
      const {sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = (sortDesc && sortDesc[0] ? '-'+sortBy[0] : (sortBy ? sortBy[0] : 'product__sku'))
      if (!sort) sort = 'product__sku'

      if (!this.$can('readAll', 'SalesNote')){
        const user = this.$store.getters.getUser
        this.filter.seller=user.id
      }
        
      const query=`page=${page || 1}&page_size=${itemsPerPage || 10}&search=${this.search}&ordering=${sort}&emited_after=${this.filter.created_after}&emited_before=${this.filter.created_before}&dte__customer__seller__id=${this.filter.seller}`
      
      try {
        const res = await fetchInvoicedProducts(query)
        this.totalItems = res.count
        this.items = res.results
      } catch (err) {
        notifications.showError(err)
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {  
      if (this.timer) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
        this.fetch()
      }, 700);
    },
    displayDetail(item){
      this.item=item
      this.toDetail=true
    } 
  },
  watch:{
    filter:{
      deep: true,
      handler() {
        if (this.options.page !== 1) {
          this.options.page = 1
        } else {
          this.fetch()
        }
      }
    },
    options:{
      handler:'fetch',
      deep:true
    },
  }
}
</script>

<style scoped>
.modern-view { background-color: #f4f7f9; min-height: 100vh; }

/* Modern Card Styles */
.modern-card { 
  border-radius: 12px !important; 
  border: 1px solid rgba(0,0,0,0.05) !important; 
  background-color: white !important; 
}
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.shadow-lg { box-shadow: 0 12px 30px rgba(0,0,0,0.1) !important; }

/* Table Styling */
.modern-table >>> thead th {
  background-color: #f8fbfc !important;
  height: 56px !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #607d8b !important;
}
.modern-table >>> td {
  height: 60px !important;
  border-bottom: 1px solid #f0f3f5 !important;
}
.clickable-rows >>> tbody tr { cursor: pointer; }
.clickable-rows >>> tbody tr:hover { background-color: #f8fbff !important; }

/* Input Styling */
.modern-input >>> .v-input__control >>> .v-input__slot {
  background-color: #f8fbfc !important;
  border-radius: 8px !important;
}
.modern-search-field { max-width: 400px; }
.modern-search-field >>> .v-input__control >>> .v-input__slot {
  background-color: white !important;
  border-radius: 12px !important;
}

/* Utilities */
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25 !important; }
.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.0px; }
.border-white-sm { border: 2px solid white !important; }
</style>